import React from 'react';
import { Trash2, Newspaper, MessageCircle } from 'lucide-react';
import { Card } from '../../atoms/Card';
import { Button } from '../../atoms/Button';
import type { Like, LikeNewsTarget, LikeCommentTarget } from '../../../services/likesService';
import { getUserProfileUrl } from '../../../utils/urlUtils';

const WEB_BASE_URL = process.env.REACT_APP_WEB_URL || 'https://sosyal.media';

const getNewsUrl = (slug: string | null, id: number): string => {
  const base = WEB_BASE_URL.endsWith('/') ? WEB_BASE_URL.slice(0, -1) : WEB_BASE_URL;
  return slug ? `${base}/i/${slug}?ig=true` : `${base}/i/${id}?ig=true`;
};

export interface LikeCardProps {
  like: Like;
  deletingId: number | null;
  onDeleteClick: (like: Like) => void;
}

export const LikeCard = ({ like, deletingId, onDeleteClick }: LikeCardProps) => {
  const isDeleting = deletingId === like.id;
  const isNews = like.targetType === 'news';
  const newsTarget = isNews ? (like.target as LikeNewsTarget) : null;
  const commentTarget = !isNews ? (like.target as LikeCommentTarget) : null;

  const handleAuthorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (like.user.username) {
      window.open(getUserProfileUrl(like.user.username), '_blank');
    }
  };

  const handleTargetClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isNews && newsTarget) {
      window.open(getNewsUrl(newsTarget.slug, newsTarget.id), '_blank');
    }
  };

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <span
              className={`flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                isNews
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-purple-100 text-purple-700'
              }`}
            >
              {isNews ? (
                <Newspaper className="w-3 h-3" />
              ) : (
                <MessageCircle className="w-3 h-3" />
              )}
              {isNews ? 'İçerik' : 'Yorum'}
            </span>
            <p className="text-gray-800 break-words line-clamp-2 leading-relaxed">
              {isNews
                ? (newsTarget?.content ?? `İçerik #${newsTarget?.id}`)
                : (commentTarget?.content ?? `Yorum #${commentTarget?.id}`)}
            </p>
          </div>
          {/* Desktop delete button */}
          <div className="hidden sm:flex flex-shrink-0 ml-4">
            <Button
              variant="danger"
              size="small"
              onClick={(e) => { e.stopPropagation(); onDeleteClick(like); }}
              disabled={isDeleting}
              isLoading={isDeleting}
              className="flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500">
            <span className="whitespace-nowrap">ID: {like.id}</span>
            <span>
            Beğenen: &nbsp;
            <button
              onClick={handleAuthorClick}
              className="whitespace-nowrap text-accent hover:underline"
            >

              {like.user.username ?? `Kullanıcı #${like.user.id}`}
            </button>
            </span>
            {isNews && newsTarget && (
              <span>
                İçerik:&nbsp;              
              <button
                onClick={handleTargetClick}
                className="whitespace-nowrap text-accent hover:underline max-w-[200px] truncate"
                title={newsTarget.title ?? `İçerik #${newsTarget.id}`}
              >
                {newsTarget.title ?? `İçerik #${newsTarget.id}`}
              </button>
              </span>
            )}
            {!isNews && commentTarget && (
              <span className="text-gray-400 max-w-[200px] truncate" title={commentTarget.content ?? ''}>
                Yorum #{commentTarget.id}
              </span>
            )}
            <span className="whitespace-nowrap">
              {new Date(like.createdOn).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          {/* Mobile delete button */}
          <div className="flex sm:hidden">
            <Button
              variant="danger"
              size="small"
              onClick={(e) => { e.stopPropagation(); onDeleteClick(like); }}
              disabled={isDeleting}
              isLoading={isDeleting}
              className="flex items-center justify-center gap-2 w-full"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
